import { type EbnSportRow, type EbnView, type EbnWarning, type EntryPhase, type EoiView, eoiSportAthletes, eoiSportMedals, impliedAthletes } from "@seagames/contracts";
import { Modal, ModalActions, useUiStrings } from "@seagames/ui";
import { type ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { invalidateEntries, submitEntryPhase } from "../../lib/api/entries";
import { toast } from "../../lib/ui/toast";
import { ChevronDownIcon, LockIcon } from "../accreditation/icons";
import { joinList } from "../fnb/parts";
import { AlertIcon, InfoIcon } from "../transport/icons";
import { type Medal, MedalDisc, PosterFoot, PosterSeal } from "./board";
import { MedalIcon } from "../common/medal-icon";
import { eoiEventsOf, eventsOf } from "./model";

/**
 * What a phase locks in, read from the SAVED view (the editor saves before it
 * opens the review): per sport its events and numbers, "—" for a number not
 * given, the totals and, for EBN, the warnings and the sports this session's
 * entries added to the EOI. The same content is the summary of a submitted
 * (or read-only) phase.
 */

type T = ReturnType<typeof useTranslation<"entries">>["t"];

/** "—" for not given. */
function useDash(): (n: number | null | undefined) => string {
	const { formatNumber: fmt } = useUiStrings();
	return (n) => (n === null || n === undefined ? "—" : fmt(n));
}

function Medals({ g, s, b }: { g: number | null; s: number | null; b: number | null }) {
	const { t } = useTranslation("entries");
	const dash = useDash();
	const one = (medal: Medal, v: number | null, word: string) => (
		<span className="ent-rv__medal" data-medal={medal}>
			<MedalIcon medal={medal} />
			<span className="sr-only">{word}</span>
			{dash(v)}
		</span>
	);
	return (
		<span className="inline-flex items-center gap-[0.5rem] tabular-nums">
			{one("gold", g, t("eoi.gold"))}
			{one("silver", s, t("eoi.silver"))}
			{one("bronze", b, t("eoi.bronze"))}
		</span>
	);
}

/** Sports start open when there are at most this many; more start folded (a NOC that picked every sport). */
const REVIEW_OPEN_MAX = 3;

interface Folds {
	isOpen: (id: string) => boolean;
	toggle: (id: string) => void;
	setAll: (open: boolean) => void;
	allOpen: boolean;
	noneOpen: boolean;
}

/** Which sports of the review are open — per sport, plus Expand / Collapse all. */
function useFolds(ids: readonly string[]): Folds {
	const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(ids.length <= REVIEW_OPEN_MAX ? ids : []));
	return {
		isOpen: (id) => open.has(id),
		toggle: (id) =>
			setOpen((prev) => {
				const next = new Set(prev);
				if (!next.delete(id)) next.add(id);
				return next;
			}),
		setAll: (on) => setOpen(new Set(on ? ids : [])),
		allOpen: ids.every((id) => open.has(id)),
		noneOpen: ids.every((id) => !open.has(id)),
	};
}

/** Expand all / Collapse all, above a list of more than one sport. */
function FoldBar({ folds, count }: { folds: Folds; count: number }) {
	const { t } = useTranslation("entries");
	if (count < 2) return null;
	return (
		<div className="flex justify-end gap-[0.375rem]">
			<button type="button" className="btn btn-ghost btn-xs" disabled={folds.allOpen} onClick={() => folds.setAll(true)}>
				{t("review.expandAll")}
			</button>
			<button type="button" className="btn btn-ghost btn-xs" disabled={folds.noneOpen} onClick={() => folds.setAll(false)}>
				{t("review.collapseAll")}
			</button>
		</div>
	);
}

/** One sport of the review: the head toggles its body (events, numbers). */
function SportFold({ id, code, name, aside, folds, children }: { id: string; code: string; name: string; aside?: ReactNode; folds: Folds; children: ReactNode }) {
	const bodyId = useId();
	const open = folds.isOpen(id);
	return (
		<li className="ent-rv__sport">
			<button type="button" className="ent-rv__sporthead ent-rv__toggle" aria-expanded={open} aria-controls={bodyId} onClick={() => folds.toggle(id)}>
				<ChevronDownIcon className="ent-rv__chev size-[1rem] shrink-0 text-gms-ink-3" />
				<span className="ent-rv__code" aria-hidden>
					{code}
				</span>
				<b className="min-w-0 flex-1 text-left text-[0.875rem] text-gms-navy-ink">{name}</b>
				{aside}
			</button>
			<div id={bodyId} hidden={!open} className="grid gap-[0.375rem]">
				{children}
			</div>
		</li>
	);
}

/** A figure tile of the totals strip. */
function Fig({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="ent-rv__fig">
			<dt>{label}</dt>
			<dd>{children}</dd>
		</div>
	);
}

export function EoiReviewContent({ view }: { view: EoiView }) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const dash = useDash();
	const chosen = view.sports.filter((s) => s.selected);
	const folds = useFolds(chosen.map((s) => s.sport_id));
	const tt = view.totals;
	return (
		<div className="grid gap-[0.875rem]">
			<dl className="ent-rv__figs">
				<Fig label={t("eoi.tiles.sports")}>{fmt(tt.sports)}</Fig>
				<Fig label={t("eoi.tiles.events")}>{fmt(tt.events)}</Fig>
				<Fig label={t("eoi.tiles.athletes")}>
					{fmt(tt.est_athletes_men + tt.est_athletes_women)}
					<small>{t("eoi.tiles.mw", { m: fmt(tt.est_athletes_men), w: fmt(tt.est_athletes_women) })}</small>
				</Fig>
				<Fig label={t("eoi.tiles.officials")}>{fmt(tt.est_officials)}</Fig>
				<Fig label={t("eoi.tiles.medals")}>
					<span className="flex items-center gap-[0.625rem]">
						<MedalDisc medal="gold" value={tt.expected_gold} label={t("board.medalCount", { medal: t("eoi.gold"), n: fmt(tt.expected_gold) })} />
						<MedalDisc medal="silver" value={tt.expected_silver} label={t("board.medalCount", { medal: t("eoi.silver"), n: fmt(tt.expected_silver) })} />
						<MedalDisc medal="bronze" value={tt.expected_bronze} label={t("board.medalCount", { medal: t("eoi.bronze"), n: fmt(tt.expected_bronze) })} />
					</span>
				</Fig>
			</dl>
			{chosen.length === 0 ? (
				<p className="text-[0.8125rem] text-gms-ink-3">{t("review.noSports")}</p>
			) : (
				<>
				<FoldBar folds={folds} count={chosen.length} />
				<ul className="grid gap-[0.625rem]" aria-label={t("review.sports")}>
					{chosen.map((s) => {
						const picked = eoiEventsOf(s).filter((e) => e.selected);
						const medals = eoiSportMedals({ ...s, events: picked });
						const athletes = eoiSportAthletes({ ...s, events: picked });
						const shown = picked.length === 0 ? s.events_total : picked.length;
						return (
							<SportFold
								key={s.sport_id}
								id={s.sport_id}
								code={s.code}
								name={s.name}
								folds={folds}
								aside={
									<>
										<span className="text-[0.75rem] text-gms-ink-3">{t("review.eventsCount", { count: shown, n: fmt(shown) })}</span>
										<Medals g={medals.expected_gold} s={medals.expected_silver} b={medals.expected_bronze} />
									</>
								}
							>
								<p className="text-[0.75rem] text-gms-ink-2">
									{t("review.sportLine", { m: dash(athletes.est_athletes_men), w: dash(athletes.est_athletes_women), o: dash(s.est_officials) })}
								</p>
								{picked.length === 0 ? (
									<p className="text-[0.75rem] text-gms-ink-3">{t("review.allEvents", { count: s.events_total, n: fmt(s.events_total) })}</p>
								) : (
									<div className="ent-rv__scroll">
										<table className="ent-rv__table">
											<thead>
												<tr>
													<th scope="col">{t("review.event")}</th>
													<th scope="col" data-num>
														{t("review.entries")}
													</th>
													<th scope="col" data-num>
														<MedalIcon medal="gold" />
														<span className="sr-only">{t("eoi.gold")}</span>
													</th>
													<th scope="col" data-num>
														<MedalIcon medal="silver" />
														<span className="sr-only">{t("eoi.silver")}</span>
													</th>
													<th scope="col" data-num>
														<MedalIcon medal="bronze" />
														<span className="sr-only">{t("eoi.bronze")}</span>
													</th>
												</tr>
											</thead>
											<tbody>
												{picked.map((e) => (
													<tr key={e.event_id}>
														<td>{e.name}</td>
														<td data-num>{dash(e.est_entries)}</td>
														<td data-num>{dash(e.expected_gold)}</td>
														<td data-num>{dash(e.expected_silver)}</td>
														<td data-num>{dash(e.expected_bronze)}</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</SportFold>
						);
					})}
				</ul>
				</>
			)}
			<p className="text-[0.71875rem] text-gms-ink-3">{t("review.dashNote")}</p>
		</div>
	);
}

/** One warning as a sentence, with the sport's and event's names. */
export function warningText(w: EbnWarning, sports: readonly EbnSportRow[], t: T): string {
	const sport = sports.find((s) => s.sport_id === w.sport_id);
	const sportName = sport?.name ?? "";
	switch (w.kind) {
		case "ABOVE_ESTIMATE": {
			const ev = sport ? eventsOf(sport).find((e) => e.event_id === w.event_id) : undefined;
			return t("warning.aboveEstimate", { count: w.needed, sport: sportName, event: ev?.name ?? "", needed: w.needed, estimated: w.estimated });
		}
		case "BELOW_ESTIMATE":
			return t("warning.belowEstimate", { count: w.implied_athletes, sport: sportName, implied: w.implied_athletes, estimated: w.estimated });
		case "NOT_IN_EOI":
			return t("warning.notInEoi", { sport: sportName });
		default:
			return t("warning.withoutEvents", { sport: sportName });
	}
}

export function EbnWarningsBox({ view }: { view: EbnView }) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	const without = view.warnings.filter((w) => w.kind === "SPORT_WITHOUT_EVENTS").map((w) => view.sports.find((s) => s.sport_id === w.sport_id)?.name ?? "");
	const others = view.warnings.filter((w) => w.kind !== "SPORT_WITHOUT_EVENTS");
	if (view.warnings.length === 0) return <p className="text-[0.8125rem] text-gms-ok">{t("warning.none")}</p>;
	return (
		<div className="flex gap-[0.625rem] rounded-field bg-gms-warn-soft p-[0.75rem] text-[0.8125rem] text-gms-warn">
			<AlertIcon className="mt-[0.0625rem] size-[1rem] shrink-0" />
			<span className="grid gap-[0.25rem]">
				<b className="font-semibold">{t("warning.title")}</b>
				{without.length > 0 && (
					<span className="text-gms-ink-2">{t("warning.sportWithoutEvents", { count: without.length, n: fmt(without.length), list: joinList(without) })}</span>
				)}
				{others.length > 0 && (
					<ul className="grid list-disc gap-[0.125rem] pl-[1rem] text-gms-ink-2">
						{others.map((w) => (
							<li key={`${w.kind}|${w.sport_id}|${"event_id" in w ? w.event_id : ""}`}>{warningText(w, view.sports, t)}</li>
						))}
					</ul>
				)}
			</span>
		</div>
	);
}

export function EbnReviewContent({ view, addedToEoi = [], showWarnings = true }: { view: EbnView; addedToEoi?: readonly string[]; showWarnings?: boolean }) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	let entered = 0;
	let entries = 0;
	let implied = 0;
	const sports = view.sports
		.map((s) => ({ sport: s, events: eventsOf(s).filter((e) => (e.entries ?? 0) > 0) }))
		.filter((x) => x.events.length > 0);
	for (const x of sports)
		for (const e of x.events) {
			entered += 1;
			entries += e.entries ?? 0;
			implied += impliedAthletes(e, e.entries ?? 0);
		}
	const added = addedToEoi.map((id) => view.sports.find((s) => s.sport_id === id)?.name).filter((n): n is string => !!n);
	const folds = useFolds(sports.map((x) => x.sport.sport_id));
	return (
		<div className="grid gap-[0.875rem]">
			<dl className="ent-rv__figs">
				<Fig label={t("ebn.tiles.sports")}>{fmt(sports.length)}</Fig>
				<Fig label={t("ebn.tiles.events")}>{fmt(entered)}</Fig>
				<Fig label={t("ebn.tiles.entries")}>{fmt(entries)}</Fig>
				<Fig label={t("ebn.tiles.implied")}>{fmt(implied)}</Fig>
			</dl>
			{added.length > 0 && (
				<div className="flex gap-[0.625rem] rounded-field bg-gms-navy-soft p-[0.75rem] text-[0.8125rem] text-gms-navy-ink">
					<InfoIcon className="mt-[0.0625rem] size-[1rem] shrink-0 text-gms-navy" />
					<span>
						<b className="block font-semibold">{t("review.addedTitle", { count: added.length })}</b>
						{joinList(added)}
					</span>
				</div>
			)}
			{showWarnings && <EbnWarningsBox view={view} />}
			{sports.length === 0 ? (
				<p className="text-[0.8125rem] text-gms-ink-3">{t("review.noEntries")}</p>
			) : (
				<>
				<FoldBar folds={folds} count={sports.length} />
				<ul className="grid gap-[0.625rem]" aria-label={t("review.sports")}>
					{sports.map(({ sport: s, events }) => (
						<SportFold
							key={s.sport_id}
							id={s.sport_id}
							code={s.code}
							name={s.name}
							folds={folds}
							aside={<span className="text-[0.75rem] text-gms-ink-3">{t("review.eventsCount", { count: events.length, n: fmt(events.length) })}</span>}
						>
							<div className="ent-rv__scroll">
								<table className="ent-rv__table">
									<thead>
										<tr>
											<th scope="col">{t("review.event")}</th>
											<th scope="col" data-num>
												{t("review.entries")}
											</th>
											<th scope="col" data-num>
												{t("review.athletes")}
											</th>
										</tr>
									</thead>
									<tbody>
										{events.map((e) => (
											<tr key={e.event_id}>
												<td>{e.name}</td>
												<td data-num>
													{e.competitor_type === "TEAM"
														? t("ebn.teamsCount", { count: e.entries ?? 0, n: fmt(e.entries ?? 0) })
														: fmt(e.entries ?? 0)}
												</td>
												<td data-num>{fmt(impliedAthletes(e, e.entries ?? 0))}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</SportFold>
					))}
				</ul>
				</>
			)}
		</div>
	);
}

/**
 * The review before Submit: the content, a confirm checkbox ("I understand
 * this locks…"), then Submit. Whether Submit is offered at all is the API's
 * `can_submit` (never re-derived here).
 */
export function ReviewSheet({
	open,
	phase,
	orgName,
	gamesId,
	organizationId,
	canSubmit,
	onClose,
	onDone,
	children,
}: {
	open: boolean;
	phase: EntryPhase;
	orgName: string;
	gamesId: string;
	organizationId: string;
	canSubmit: boolean;
	onClose: () => void;
	onDone: () => void;
	children: ReactNode;
}) {
	const { t } = useTranslation("entries");
	const [busy, setBusy] = useState(false);
	const [sure, setSure] = useState(false);
	const checkId = useId();
	useEffect(() => {
		if (open) setSure(false);
	}, [open]);
	const submit = async () => {
		setBusy(true);
		try {
			await submitEntryPhase(phase, { games_id: gamesId, organization_id: organizationId });
			await invalidateEntries();
			onClose();
			onDone();
		} catch (err) {
			toast.error(err);
		} finally {
			setBusy(false);
		}
	};
	return (
		<Modal
			open={open}
			onClose={onClose}
			dismissible={!busy}
			size="lg"
			title={t("review.title", { phase: t(`phase.${phase}`) })}
			description={orgName}
			footer={
				<div className="grid gap-[0.625rem]">
					{canSubmit ? (
						<label htmlFor={checkId} className="flex items-start gap-[0.5rem] text-[0.8125rem] text-gms-ink-2">
							<input id={checkId} type="checkbox" className="checkbox checkbox-sm mt-[0.0625rem]" checked={sure} onChange={(e) => setSure(e.target.checked)} />
							<span>{phase === "EOI" ? t("review.confirmEoi") : t("review.confirmEbn")}</span>
						</label>
					) : (
						<p className="text-[0.8125rem] text-gms-ink-3">{t("review.cannotSubmit")}</p>
					)}
					<ModalActions>
						<button type="button" className="btn btn-line" onClick={onClose} disabled={busy}>
							{t("review.back")}
						</button>
						{canSubmit && (
							<button type="button" className="btn btn-accent" onClick={() => void submit()} disabled={busy || !sure}>
								{busy ? <span className="loading loading-spinner loading-xs" /> : <LockIcon className="size-[1rem]" />}
								{t("review.submit")}
							</button>
						)}
					</ModalActions>
				</div>
			}
		>
			<div className="grid gap-[0.75rem]">
				<div className="flex gap-[0.625rem] rounded-field bg-gms-navy-soft p-[0.75rem] text-[0.8125rem] leading-snug text-gms-navy-ink">
					<InfoIcon className="mt-[0.0625rem] size-[1rem] shrink-0 text-gms-navy" />
					<span>
						<b className="block font-semibold">{t("review.lockTitle")}</b>
						{phase === "EOI" ? t("review.lockEoi") : t("review.lockEbn")}
					</span>
				</div>
				{children}
			</div>
		</Modal>
	);
}

/**
 * A submitted or read-only phase: the review content under a head with the
 * NOC, the phase and the submitted seal; the foot offers what the caller may
 * still do (Reopen for a SUPER_ADMIN, "Change anyway" when the API still lets
 * them edit).
 */
export function EntrySummary({
	phase,
	view,
	onEdit,
	onReopen,
	children,
}: {
	phase: EntryPhase;
	view: { organization: { name: string; code: string | null }; submission: EoiView["submission"] };
	onEdit?: () => void;
	onReopen?: () => void;
	children: ReactNode;
}) {
	const { t } = useTranslation("entries");
	const h = useId();
	return (
		<section className="ent-summary" aria-labelledby={h}>
			<header className="ent-poster__head">
				<div className="grid min-w-0 gap-[0.125rem]">
					<h2 id={h} className="ent-poster__title">
						{view.organization.code && <span className="ent-poster__code">{view.organization.code}</span>}
						{view.organization.name}
					</h2>
					<p className="text-[0.78125rem] opacity-85">{t(`phase.${phase}`)}</p>
				</div>
				<PosterSeal submission={view.submission} />
			</header>
			<div className="ent-summary__body">{children}</div>
			<PosterFoot submission={view.submission} onEdit={onEdit} onReopen={onReopen} />
		</section>
	);
}
